'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { Box, Container } from '@mui/material'
import { Toaster } from 'react-hot-toast'

import { useWallet, useWalletContentData } from '@/src/hooks/wallet'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import { AccountsSidePanelWrapper } from '@/src/components/desktop/accounts/sidePanel'
import { MultiBrandWalletInfo, WalletTransactionsResponse } from '@/src/types/wallet'
import { WalletFAQCollection } from '@/src/types/walletFaq'

import WalletDesktopFlyinContent from './WalletDesktopFlyinContent'
import WalletDesktopCleartripContent from './WalletDesktopCleartripContent'
import { WalletFAQ } from '../../mobile/wallet/walletFAQ'

// Dynamically load SignInModal
const SignInModalWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "signin-modal" */ '@/src/components/common/header/variants/flyin/SignInModal/SignInModalWrapper'
        ),
    { ssr: false }
)

interface WalletDesktopPageWrapperProps {
    initialWalletInfo?: MultiBrandWalletInfo | null
    initialTransactions?: WalletTransactionsResponse | null
    initialFAQs?: WalletFAQCollection | null
    domainCountry?: string
    isMultiBrand?: boolean
}

const WalletDesktopPageWrapper: React.FC<WalletDesktopPageWrapperProps> = ({
    initialWalletInfo,
    initialTransactions,
    initialFAQs,
    domainCountry,
    isMultiBrand: isMultiBrandSsr = false
}) => {
    const isMultiBrand = isMultiBrandSsr

    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('wallet')
    const tCommon = useTranslations('common')
    const aboutWalletLabel = t('aboutWalletGeneric')

    const [modalOpen, setModalOpen] = useState(false)

    // Use wallet hook with initial SSR data
    const wallet = useWallet({
        initialWalletInfo,
        initialTransactions,
        isMultiBrand
    })

    const isRtl = locale === 'ar'

    const {
        formattedTransactions,
        cleartripFormattedTransactions,
        translatedBalanceError,
        translatedTransactionsError,
        formatAmount,
        cleartripTransactionLabels,
        brandFilter,
        setBrandFilter,
        brandBalances
    } = useWalletContentData({ wallet, locale })

    // Handler for FAQ section
    const handleOpenFAQ = useCallback(() => {
        // Scroll to FAQ section (which is rendered below)
        if (typeof window !== 'undefined') {
            const faqElement = document.getElementById('wallet-faq-section')
            if (faqElement) {
                faqElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        }
    }, [])

    // Show sign-in modal if not logged in
    useEffect(() => {
        if (typeof window !== 'undefined' && !wallet.isLoggedIn) {
            setModalOpen(true)
        }
    }, [wallet.isLoggedIn])

    // Handlers
    const handleModalClose = useCallback(() => {
        setModalOpen(false)
        // Redirect if still not logged in
        if (!wallet.isLoggedIn) {
            router.push(`/${locale}`)
        }
    }, [wallet.isLoggedIn, router, locale])

    const baseWalletPageProps = useMemo(
        () => ({
            displayBalance: wallet.displayBalance,
            displayCurrency: wallet.displayCurrency,
            domainBalances: wallet.domainBalances,
            hasMultipleDomainBalances: wallet.hasMultipleDomainBalances,
            hasMoreTransactions: wallet.hasMoreTransactions,
            brandBalances,
            activeTab: wallet.activeTab,
            onTabChange: wallet.setActiveTab,
            brandFilter,
            onBrandFilterChange: setBrandFilter,
            isLoadingBalance: wallet.isLoadingBalance,
            isFetchingMore: wallet.isFetchingTransactions,
            balanceError: translatedBalanceError,
            transactionsError: translatedTransactionsError,
            onLoadMore: wallet.loadMoreTransactions,
            onRefresh: wallet.refreshWallet,
            onOpenFAQ: handleOpenFAQ,
            formatAmount,
            t,
            tCommon,
            locale,
            isRtl,
            aboutWalletLabel,
            showMultiBrandWallet: isMultiBrand
        }),
        [
            wallet.displayBalance,
            wallet.displayCurrency,
            wallet.domainBalances,
            wallet.hasMultipleDomainBalances,
            wallet.hasMoreTransactions,
            brandBalances,
            wallet.activeTab,
            wallet.setActiveTab,
            brandFilter,
            setBrandFilter,
            wallet.isLoadingBalance,
            wallet.isFetchingTransactions,
            translatedBalanceError,
            translatedTransactionsError,
            wallet.loadMoreTransactions,
            wallet.refreshWallet,
            handleOpenFAQ,
            formatAmount,
            t,
            tCommon,
            locale,
            isRtl,
            aboutWalletLabel,
            isMultiBrand
        ]
    )

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'grey.100'
            }}
        >
            <Toaster position="bottom-right" reverseOrder={false} gutter={8} />
            {/* Desktop Header */}
            <HeaderContainer domainCountry={domainCountry} />

            {/* Main Content */}
            <Box sx={{ flex: 1, bgcolor: 'grey.50' }}>
                <Container
                    maxWidth="xl"
                    disableGutters
                    sx={{
                        py: { xs: 2, md: 3 },
                        px: { xs: 2, md: 3 }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: 2, md: 3 },
                            alignItems: 'flex-start'
                        }}
                    >
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <AccountsSidePanelWrapper activeIndex={1} />
                        </Box>
                        <Box sx={{ flex: 1, maxWidth: 820 }}>
                            {wallet.isCleartrip ? (
                                <WalletDesktopCleartripContent
                                    walletPageProps={{
                                        ...baseWalletPageProps,
                                        formattedTransactions: [],
                                        isLoadingTransactions: false
                                    }}
                                    transactions={cleartripFormattedTransactions}
                                    hasMoreTransactions={wallet.hasMoreTransactions}
                                    isFetchingMore={wallet.isFetchingTransactions}
                                    onLoadMore={wallet.loadMoreTransactions}
                                    formatAmount={formatAmount}
                                    {...cleartripTransactionLabels}
                                />
                            ) : (
                                <WalletDesktopFlyinContent
                                    {...baseWalletPageProps}
                                    formattedTransactions={formattedTransactions}
                                    cleartripFallbackTransactions={cleartripFormattedTransactions}
                                    cleartripTransactionLabels={cleartripTransactionLabels}
                                    isLoadingTransactions={wallet.isLoadingTransactions}
                                />
                            )}
                            <Box id="wallet-faq-section">
                                <WalletFAQ
                                    initialFAQs={initialFAQs}
                                    isDesktop
                                    isCleartripBrand={wallet.isCleartrip}
                                    isMultiBrand={isMultiBrand}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Sign In Modal */}
            {modalOpen && <SignInModalWrapper open={modalOpen} onClose={handleModalClose} />}
        </Box>
    )
}

export { WalletDesktopPageWrapper }
export default WalletDesktopPageWrapper
